import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {List} from 'immutable';

import {
  type CheckerState,
  checkType,
  Component,
  EvalEnv,
  ExpressionPath,
  Expressions,
  Plugins,
  TypeCheckerState,
  Types,
  Variable,
  withSystem,
  TypeImplementation,
  matchExpression,
  ExpressionPathPart,
} from '@packages/systems/wfdl';
import {
  EEnum,
  EList,
  ENumber,
  ERecord,
  EText,
  Expression,
  TBoolean,
  TNumber,
  TText,
  TTypeConstructor,
  ECall,
  EVariable,
} from '@packages/systems/wfdl-ast';
import {BetaFeatureStateType} from '@packages/systems/designer/beta-features';
import {CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import {Dispatch} from '@packages/systems/flux';
import {LocalizationState} from '@packages/systems/localization-types';
import {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {CollectionItemsLoader} from '@packages/systems/cms/client';
import {
  CLOSED_STATE,
  OPENED_STATE,
  DeprecatedPopoverBridge,
} from '@packages/systems/design-system/PopoverBridge';
import {emotionStyled} from '@packages/utilities/styled';
import {
  rightSidebarLabelWidth,
  rightSidebarWidth,
} from '@packages/systems/design-system/constants/DesignerUI';
import {
  HStack,
  Select,
  Spacer,
  Text,
  TextInput,
  View,
  tokens,
  Tooltip,
  WarningTriangleIcon,
  type SelectOption,
  type SelectOptionGroup,
} from '@packages/systems/spring';
import PropertyList, {
  PropertyListAddButton,
  PropertyListItem,
  PropertyListItemID,
} from '@packages/systems/ui/shared-ui/PropertyList';
import {
  BindingSource,
  getPageBindingContext,
  type ConsolidatedBindingContext,
} from '@packages/systems/dynamo/gateway';
import {
  getOperatorById,
  isComparisonOperator,
  mapFieldsQuery,
  OPERATOR_EQ,
  OPERATOR_EXISTS,
  OPERATOR_GTE,
  OPERATOR_LTE,
  OPERATOR_NOT_EXISTS,
  OperatorId,
} from '@packages/systems/dynamo/utils/DynQueryUtils';
import {QUERY_FILTER_FOR_STATES} from '@packages/systems/dynamo/constants';
import {
  convertItemRefFilterOperatorForPersisting,
  convertItemRefFilterOperatorForRendering,
  FauxBindingSource,
  getBindingSource,
  getFieldPath,
  getReferencedItemIds,
  HumanTexts,
  isReferenceBindingSource,
  DynQueryParamType,
  DynQueryParam,
  getFilterBindingSources,
  useHumanTextsWithBindingContexts,
} from '@packages/systems/dynamo/utils/ParamPanelUtils';
import {parseQuery} from '@packages/systems/dynamo/utils/ParamUtils';
import {
  normalizePropsForRendering,
  getAvailableDateTimeTenses,
  getAvailableDateUnitOptions,
  isValueRequired,
  getItemRefOptions,
  getOptionOptions,
  getInitialValues,
  getOperatorOptions,
  fieldChangeHandler,
  type ParamPanelState,
  type AvailableFieldOptions,
  type FieldOption,
  normalizePropsForRenderingWithBindingContexts,
} from '@packages/systems/dynamo/utils/ParamPanelNormalizers';
import NumberInput from '@packages/systems/design-system/WebflowNumberInput';
import {createFieldPath} from '@packages/systems/dynamo/utils/ParamFieldPathUtils';
import {FieldListIcon} from '@packages/systems/ui/shared-ui/FieldListIcon';
import {
  ExpressionBindMetaPayload,
  ExpressionEditorActionPayload,
  getBindingLabel,
  ExpressionBinding,
  getBindingIcon,
} from '@packages/systems/ExpressionEditor-utils';
import {
  iconName,
  identifier,
  namesEqual,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {
  ContextualExpression,
  ContextualExpressions,
  stripAll,
  stripAnn,
  EDistinct as ContextualEDistinct,
  annotate,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {
  selectComponents,
  selectCurrentComponent,
  selectCurrentComponentType,
  selectCurrentPage,
} from '@packages/utilities/platform-selectors';
import {selectCollectionState} from '@packages/systems/cms/client-selectors';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {selectPageState} from '@packages/systems/designer/page';
import {getDynPages} from '@packages/shared/utils/PageTreeUtils';
import {deleteAt} from '@packages/utilities/array';
import {SymbolBindingDot} from '@packages/systems/ui/shared-ui/SymbolBindingDot';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {constantFalse, identity} from '@packages/utilities/fp/utils';
import {pageComponentType} from '@packages/utilities/platform';

import AsyncItemReferenceInput from './AsyncItemReferenceInput';
import {numberValueCreator, numberToTextVariable} from './numberValueCreator';
import {getDefaultForComponentProp} from '../dataTypeUtils';
import {getFlexStyles} from '../collectionListUtils';
import {
  getCollectionListPropTypeName,
  createCollectionListPropType,
} from '@packages/systems/dynamo/utils/CollectionListPropNameUtils';

const textIcon = qualify(Plugins.name('Basic'), iconName('Text'));

const selectBetaFeatureState = (state: DesignerStores) =>
  state.BetaFeatureStore;

const selectLocalizationState = (state: DesignerStores) =>
  state.LocalizationStore;

const selectPluginsFromState = (state: DesignerStores) =>
  state.DesignerStore.plugins;

const filterForPath: ExpressionPath = [{in: 'Record', at: 'filterFor'}];
const fieldsPathPart: ExpressionPathPart = {in: 'Record', at: 'fields'};
const fieldsPath: ExpressionPath = [fieldsPathPart];

export const CollectionListFilterRender =
  ({collectionId}: {collectionId: string}) =>
  ({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: TypeImplementation.ExpressionEditorDispatch;
  }) => {
    return (
      <CollectionListFilterComponent
        expr={expr}
        dispatch={dispatch}
        collectionId={collectionId}
      />
    );
  };

const CollectionListFilterComponent = ({
  expr,
  dispatch,
  collectionId,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
  collectionId: string;
}) => {
  const [rootCheckerState, _] = React.useContext(TypeCheckerState);
  const customDispatch = useDispatch();
  const componentMap = useDesignerSelector(selectComponents);
  const currentComponent = useDesignerSelector(selectCurrentComponent);
  const currentComponentType = useDesignerSelector(selectCurrentComponentType);
  const selectedCurrentPage = useDesignerSelector(selectCurrentPage);
  const currentPage =
    selectedCurrentPage === undefined ? null : selectedCurrentPage;
  const pageState = useDesignerSelector(selectPageState);
  const dynamicPages = getDynPages(pageState);
  const collectionPageForCollection = dynamicPages.find(
    (page) => page.dynCollection === collectionId
  );
  const collectionState = useDesignerSelector(selectCollectionState);
  const betaFeatures = useDesignerSelector(selectBetaFeatureState);
  const localizationState = useDesignerSelector(selectLocalizationState);
  const plugins = useDesignerSelector(selectPluginsFromState);

  if (!currentPage || !collectionPageForCollection) return null;
  const bindingContext = getPageBindingContext(
    collectionPageForCollection,
    collectionState.collections
  );
  // Only get the parent binding context if we're on the page component
  const parentBindingContext = namesEqual(
    currentComponentType,
    pageComponentType
  )
    ? getPageBindingContext(currentPage, collectionState.collections)
    : null;

  const ann = getAnn(expr);
  const isBinding = ContextualExpressions.isBinding(stripAnn(expr));

  const strippedExpr = stripAll(expr);
  const defaultValueExpr = getDefaultForComponentProp(
    plugins,
    currentComponent,
    strippedExpr
  );

  // If expression is already a binding, use it as-is. Otherwise, wrap it in a Distinct
  // expression to ensure proper type checking and binding capabilities for the filter data.
  const exprAsUnique = isBinding
    ? expr
    : annotate(
        ContextualEDistinct({
          name: getCollectionListPropTypeName('Filter', collectionId),
          value: expr,
        }),
        {
          ...ann,
          env: {
            ...ann.env,
            // Ensure there is no duplicate `in distinct` key
            expressionPath: ann.env.expressionPath.slice(0, -1),
          },
          type: withSystem(
            {
              isBindable: true,
              label: 'Filters',
              labelVariant: 'hidden',
            },
            createCollectionListPropType('Filter', collectionId)
          ),
        }
      );
  return (
    <DynListFilterSettingsCLP
      dispatch={customDispatch}
      bindingContext={bindingContext}
      parentBindingContext={parentBindingContext}
      plugins={plugins}
      stores={{
        collection: collectionState,
        betaFeatures,
        localizationStore: localizationState,
      }}
      currentComponent={currentComponent}
      componentMap={componentMap}
      currentPage={currentPage}
      checkerState={rootCheckerState}
      expressionEditorDispatch={dispatch}
      strippedExpr={strippedExpr}
      expr={expr}
      exprAsUnique={exprAsUnique}
      defaultValueExpr={defaultValueExpr}
    />
  );
};

const StyledPill = emotionStyled(View)({
  backgroundColor: tokens.colors.purpleTransparent,
  padding: '$0.25 $0.5',
  borderRadius: '$1',
  color: tokens.colors.purpleText,
  display: 'inline-flex',
  maxWidth: '100%',
  minWidth: 0,
  '&:hover': {
    backgroundColor: tokens.colors.purpleTransparentHover,
  },
});

const IconWrapper = emotionStyled(Plugins.PluginIcon)({
  color: tokens.colors.purpleText,
});

const FilterRow = emotionStyled(HStack)({
  minWidth: 0,
  width: '100%',
  flexWrap: 'nowrap',
  gap: '$0.5',
});

const FieldLabel = emotionStyled(Text)({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const CollectionListBindingPill = ({
  label,
  icon,
  style,
}: {
  label: string;
  icon: Plugins.QualifiedPluginIcon;
  style?: React.CSSProperties;
}) => {
  return (
    <StyledPill
      data-automation-id={`filter-binding-pill-${label}`}
      style={style}
    >
      <FilterRow>
        <IconWrapper icon={icon} />
        <FieldLabel>{label}</FieldLabel>
      </FilterRow>
    </StyledPill>
  );
};

const TwoColumns = emotionStyled('div')({
  display: 'grid',
  gridTemplateColumns: `${rightSidebarLabelWidth}px minmax(0, auto)`,
  gridGap: '$2',
  alignItems: 'center',
});

const StyledInputRow = emotionStyled(TwoColumns)({
  marginTop: '$2',
});

const InputGroup = emotionStyled('div')({
  display: 'flex',
  gap: '$2',
  '> *': {
    flex: 1,
  },
});

const SelectInputContainer = emotionStyled('div')({
  position: 'relative',
});

type FilterItem = {
  fieldPath: string;
  operator: OperatorId;
  value: string | number | null;
  value2: string | number | null;
  value3: string | number | null;
  fieldType: string;
};

type DynListFilterSettingsProps = {
  dispatch: Dispatch;
  bindingContext: ConsolidatedBindingContext | null;
  parentBindingContext: ConsolidatedBindingContext | null;
  plugins: Plugins.PluginsType;
  stores: {
    collection: CollectionStateType;
    betaFeatures: BetaFeatureStateType;
    localizationStore: LocalizationState;
  };
  currentComponent: Component.ComponentType;
  componentMap: Component.ComponentMap;
  currentPage: PageRecordType | null;
  checkerState: CheckerState;
  expressionEditorDispatch: (action: ExpressionEditorActionPayload) => void;
  expr: ContextualExpression;
  exprAsUnique: ContextualExpression;
  strippedExpr: Expression;
  defaultValueExpr: Expression;
};

type QueryFilterStateType =
  (typeof QUERY_FILTER_FOR_STATES)[keyof typeof QUERY_FILTER_FOR_STATES];

const QUERY_FILTER_OPTIONS = [
  {label: 'Match all', value: QUERY_FILTER_FOR_STATES.ALL},
  {label: 'Match any', value: QUERY_FILTER_FOR_STATES.ANY},
];

const QueryFilterForDropdown = ({
  onChange,
  value,
}: {
  onChange: (val: QueryFilterStateType) => void;
  value: QueryFilterStateType;
}) => {
  return (
    <View
      display="flex"
      alignItems="center"
      data-automation-id="query-filter-for-dropdown-wrap"
    >
      <Select
        variant="outline"
        data-automation-id="query-filter-for-select"
        id="query-filter-for-select"
        onChange={onChange}
        value={value}
        options={QUERY_FILTER_OPTIONS}
        shouldPositionInline
      />
    </View>
  );
};

type ConditionValueComponentProps = {
  value?: string | number | null;
  value2?: string | number | null;
  value3?: string | number | null;
  inputValue?: string;
  operator: OperatorId;
  handleDispatch: (params: {
    value?: string | number;
    value2?: string;
    value3?: string;
    operator?: OperatorId;
  }) => void;
  setInputValue?: (value: string) => void;
  normalizedProps?: ReturnType<typeof normalizePropsForRendering>;
};

const DATE_UNIT_OPTIONS = getAvailableDateUnitOptions().map((unit) => ({
  label: unit,
  value: unit,
}));
const DATE_TIME_TENSE_OPTIONS = getAvailableDateTimeTenses().map((tense) => ({
  label: tense.name,
  value: tense.id,
}));

const DateConditionValue = ({
  inputValue = '',
  operator,
  value2,
  value3,
  handleDispatch,
  setInputValue = () => {},
}: ConditionValueComponentProps) => {
  const safeNumericValue = (val: string | number | null) => {
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  };
  const isTodayDateFilter =
    (operator === OPERATOR_GTE || operator === OPERATOR_LTE) &&
    safeNumericValue(inputValue) === 0;

  return (
    <View display="flex" flexDirection="column">
      <StyledInputRow>
        <span>Value</span>
        <InputGroup>
          <NumberInput
            data-automation-id="filter-value-input"
            value={safeNumericValue(inputValue)}
            min={0}
            max={Number.MAX_SAFE_INTEGER}
            step={1}
            onChange={(newValue) => {
              // drop floating part
              const stringValue = String(Math.floor(newValue));
              handleDispatch({value: stringValue});
              setInputValue(stringValue);
            }}
            alwaysShowRightInputTicks
          />
          <Select
            data-automation-id="filter-date-interval-select"
            value={String(value2)}
            onChange={(selectedValue) =>
              handleDispatch({value2: selectedValue})
            }
            options={DATE_UNIT_OPTIONS}
          />
        </InputGroup>
      </StyledInputRow>
      <StyledInputRow>
        <Select
          data-automation-id="filter-date-time-tense-select"
          value={String(value3)}
          onChange={(selectedValue) => handleDispatch({value3: selectedValue})}
          options={DATE_TIME_TENSE_OPTIONS}
          renderTriggerButtonLabel={
            isTodayDateFilter ? () => 'today' : undefined
          }
          isDisabled={isTodayDateFilter}
          style={{gridColumn: '2'}}
        />
      </StyledInputRow>
    </View>
  );
};

const OptionConditionValue = ({
  value,
  handleDispatch,
}: ConditionValueComponentProps) => {
  const {bindingSource} = useFilterEntryContext();
  if (bindingSource.type !== 'Option') return null;

  const options = bindingSource.meta.options.map(
    (option: {name: string; id: string}) => ({
      label: option.name,
      value: option.id,
    })
  );

  return (
    <View display="flex" flexDirection="column">
      <StyledInputRow>
        <span>Value</span>
        <SelectInputContainer>
          <Select
            data-automation-id="filter-option-value-select"
            options={options}
            value={String(value)}
            onChange={(selectedValue) => handleDispatch({value: selectedValue})}
          />
        </SelectInputContainer>
      </StyledInputRow>
    </View>
  );
};

const NumberConditionValue = ({
  inputValue = '',
  handleDispatch,
  setInputValue = () => {},
}: ConditionValueComponentProps) => {
  const safeNumericValue = (val: string | number | null) => {
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  };

  return (
    <StyledInputRow>
      <span>Value</span>
      <ValueBinder bindingSourceMapping={bindingSourceToTypeMapping.Number}>
        <NumberInput
          data-automation-id="filter-value-input"
          value={safeNumericValue(inputValue)}
          min={Number.MIN_SAFE_INTEGER}
          max={Number.MAX_SAFE_INTEGER}
          onChange={(newValue) => {
            const stringValue = String(newValue);
            setInputValue(stringValue);
            handleDispatch({value: newValue});
          }}
          step={1}
          precision={8}
        />
      </ValueBinder>
    </StyledInputRow>
  );
};

const ItemRefConditionValue = ({
  value,
  operator,
  handleDispatch,
  normalizedProps,
}: ConditionValueComponentProps) => {
  const {parentBindingContext, stores, bindingSource} = useFilterEntryContext();

  const handleValueChange = useCallback(
    (newValue: string | undefined) => {
      if (newValue !== null) {
        handleDispatch({value: newValue});
      }

      // Convert operators for ItemRef fields to handle single/multiple references correctly
      // For ItemRef fields, we need to convert between single-item operators (EQ/NE)
      // and multi-item operators (IDIN/IDNIN) based on whether the referenced field
      // contains single or multiple items
      const convertedOperator: OperatorId =
        bindingSource.type === 'ItemRef'
          ? convertItemRefFilterOperatorForPersisting(
              operator,
              newValue ?? null,
              parentBindingContext
            )
          : operator;

      handleDispatch({value: newValue, operator: convertedOperator});
    },
    [bindingSource.type, handleDispatch, operator, parentBindingContext]
  );

  if (
    !normalizedProps ||
    !stores ||
    (bindingSource.type !== 'ItemRef' && bindingSource.type !== 'ItemRefSet')
  ) {
    return null;
  }

  return (
    <StyledInputRow>
      <span>Value</span>
      <SelectInputContainer>
        <AsyncItemReferenceInput
          availableItemRefOptions={normalizedProps.availableItemRefOptions}
          bindingSource={bindingSource}
          itemRefOptionLabel={normalizedProps.itemRefOptionLabel}
          onValueChange={handleValueChange}
          stores={stores}
          value={value?.toString() ?? ''}
        />
      </SelectInputContainer>
    </StyledInputRow>
  );
};

const TextConditionValue = ({
  inputValue = '',
  handleDispatch,
  setInputValue = () => {},
}: ConditionValueComponentProps) => {
  const handleInput = (value: string) => {
    const trimmedValue = value.trim();
    handleDispatch({value: trimmedValue});
  };

  return (
    <StyledInputRow>
      <span>Value</span>
      <ValueBinder bindingSourceMapping={bindingSourceToTypeMapping.Text}>
        <TextInput
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
          }}
          onBlur={() => handleInput(inputValue)}
          onKeyDown={(e) => e.key === 'Enter' && handleInput(inputValue)}
          data-automation-id="filter-value-input"
        />
      </ValueBinder>
    </StyledInputRow>
  );
};

const IdConditionValue = ({normalizedProps}: ConditionValueComponentProps) => {
  const {bindingSource} = useFilterEntryContext();

  if (bindingSource.type !== 'Id') return null;

  const collectionName = normalizedProps?.collectionName || 'Item';

  return (
    <StyledInputRow>
      <span>Value</span>
      <View
        display="flex"
        alignItems="center"
        padding="$1"
        borderRadius="$2"
        border="$border1"
        data-automation-id="filter-id-input"
      >
        <i className="icon-main lightning" />
        <View marginLeft="$1">Current {collectionName}</View>
      </View>
    </StyledInputRow>
  );
};

const conditionValueComponents: Record<
  string,
  React.FC<ConditionValueComponentProps>
> = {
  Date: DateConditionValue,
  Option: OptionConditionValue,
  Number: NumberConditionValue,
  CommercePrice: NumberConditionValue,
  ItemRef: ItemRefConditionValue,
  ItemRefSet: ItemRefConditionValue,
  Id: IdConditionValue,
};

const getValueOrDefault =
  (defaultExpr: Expression) =>
  (expr: Expression | undefined): Expression =>
    expr ?? defaultExpr;

const getDefault = (defaultExpr: Expression) => (): Expression => defaultExpr;

type BindingTypeData = {
  fieldType: typeof TNumber | typeof TBoolean | typeof TText;
  valueMapper: (arg1: Expression | undefined, arg2: HumanTexts) => Expression;
  resetValue: (bindingSource: BindingSource | FauxBindingSource) => Expression;
  updateOperator?: (
    path: ExpressionPath,
    fieldName: string,
    binding: Expression
  ) => [ExpressionPath, Expression];
};

const bindingSourceToTypeMapping: Record<
  'Text' | 'Number' | 'Bool' | 'IsSet',
  BindingTypeData
> = {
  Text: {
    fieldType: TText,
    valueMapper: getValueOrDefault(EText('')),
    resetValue: getDefault(EText('')),
  },
  Number: {
    fieldType: TNumber,
    valueMapper: numberValueCreator,
    updateOperator: (path, _fieldName, binding) => {
      // Unique Filter types value as Text — wrap Number bindings.
      const newBinding = ECall({
        fun: EVariable(numberToTextVariable),
        arg: binding,
      });
      return [path, newBinding];
    },
    resetValue: getDefault(ENumber(0)),
  },
  Bool: {
    fieldType: TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('TriStateBoolean'))
    ),
    resetValue: getDefault(EEnum('true')),
    valueMapper: (expr: Expression | undefined) => {
      if (expr) {
        // Get string value of the expr and map to an enum
        const txtVal = Expressions.getText(expr);
        if (txtVal === 'true' || txtVal === 'false') {
          return EEnum(txtVal);
        }
        // If current expr is binding, return that
        if (Expressions.isBinding(expr)) {
          return expr;
        }
      }
      // fallback to default expr
      return EEnum('true');
    },
    updateOperator: (path, fieldName, binding) => {
      const newPath = path.slice(0, -1);
      const newBinding = ERecord({
        fieldPath: EText(fieldName),
        operatorName: EText(getOperatorById(OPERATOR_EQ).name),
        value: binding,
      });
      return [newPath, newBinding];
    },
  },
  IsSet: {
    fieldType: TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('TriStateBoolean'))
    ),
    resetValue: getDefault(EEnum('true')),
    valueMapper: (expr: Expression | undefined) => {
      if (expr && Expressions.isBinding(expr)) {
        return expr;
      }
      // fallback to default expr
      return EEnum('true');
    },
  },
};

type ConditionValueProps = {
  listItem: {item: FilterItem; id: string};
  normalizedProps: ReturnType<typeof normalizePropsForRendering>;
};

const ConditionValue = ({
  listItem: {
    item: {fieldPath, operator, value, value2, value3},
  },
  normalizedProps,
}: Omit<ConditionValueProps, 'inputValue' | 'setInputValue'>) => {
  const {listIdx, bindingSource, expressionEditorDispatch} =
    useFilterEntryContext();
  const [inputValue, setInputValue] = useState(
    String(normalizedProps.value) || ''
  );

  useEffect(() => {
    setInputValue(String(normalizedProps.value) || '');
  }, [normalizedProps.value]);

  const handleDispatch = useCallback(
    (params: {
      value?: string | number;
      value2?: string;
      value3?: string;
      operator?: OperatorId;
    }) => {
      const formattedField = mapFieldsQuery(
        params.operator ?? operator,
        params.value ?? value,
        params.value2 ?? value2,
        params.value3 ?? value3,
        bindingSource.type
      );

      // Ensure the value we save is a string
      const valueAsString = String(formattedField.value);
      expressionEditorDispatch({
        type: 'EXPRESSION_ACTION',
        key: [fieldsPathPart, {in: 'List', index: listIdx}],
        payload: {
          newExpression: ERecord({
            fieldPath: EText(fieldPath),
            operatorName: EText(formattedField.operatorName),
            value: EText(valueAsString),
          }),
        },
      });
    },
    [
      expressionEditorDispatch,
      listIdx,
      fieldPath,
      operator,
      value,
      value2,
      value3,
      bindingSource.type,
    ]
  );

  const FieldComponent: React.FC<ConditionValueComponentProps> =
    conditionValueComponents[bindingSource.type] ?? TextConditionValue;

  return (
    <FieldComponent
      value={value}
      value2={value2}
      value3={value3}
      operator={operator}
      inputValue={inputValue}
      handleDispatch={handleDispatch}
      setInputValue={setInputValue}
      normalizedProps={normalizedProps}
    />
  );
};

type FilterLabelProps = {
  bindingIcon: Plugins.QualifiedPluginIcon;
  bindingLabel: string;
  isReferencedItemLoading?: boolean;
  isFieldBinding: boolean;
};

const OperatorText = emotionStyled(Text)({
  flex: '0 0 auto',
});

const FilterLabel = ({
  bindingIcon,
  bindingLabel,
  isReferencedItemLoading,
  isFieldBinding,
}: FilterLabelProps) => {
  const {humanTexts} = useFilterEntryContext();
  const {fieldText, operatorBoundSymbol, operatorNonBoundSymbol, valueText} =
    humanTexts;

  if (isReferencedItemLoading) return <Text>Loading referenced item...</Text>;

  const containerProps = {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: tokens.space[1],
    width: '100%',
  };

  if (isFieldBinding) {
    const fieldLength = fieldText?.length ?? 0;
    const valueLength = bindingLabel?.length ?? 0;

    return (
      <View {...containerProps}>
        <Text
          colorVariant="secondary"
          style={getFlexStyles(fieldLength, valueLength)}
        >
          {fieldText}
        </Text>{' '}
        <OperatorText>{operatorBoundSymbol}</OperatorText>{' '}
        <CollectionListBindingPill
          label={bindingLabel}
          icon={bindingIcon}
          style={getFlexStyles(valueLength, fieldLength)}
        />
      </View>
    );
  }

  const fieldLength = fieldText?.length ?? 0;
  const valueLength = valueText?.length ?? 0;

  return (
    <View {...containerProps}>
      <Text
        colorVariant="secondary"
        style={getFlexStyles(fieldLength, valueLength)}
      >
        {fieldText}
      </Text>{' '}
      <OperatorText>{operatorNonBoundSymbol}</OperatorText>
      {valueLength ? ' ' : null}
      {valueLength ? (
        <Text style={getFlexStyles(valueLength, fieldLength)}>{valueText}</Text>
      ) : null}
    </View>
  );
};

// Define the shape of the data
interface FilterEntryContextType {
  initialCheckerState: CheckerState;
  expressionEditorDispatch: (action: ExpressionEditorActionPayload) => void;
  listIdx: number;
  fieldPath: string;
  stores: {
    collection: CollectionStateType;
    localizationStore: LocalizationState;
    betaFeatures: BetaFeatureStateType;
  };
  parentBindingContext: ConsolidatedBindingContext;
  bindingSource: BindingSource | FauxBindingSource;
  operatorId: OperatorId;
  dispatch: Dispatch;
  getFieldInitialValues: (
    bindingSource: BindingSource | FauxBindingSource
  ) => any;
  availableFieldOptions: AvailableFieldOptions;
  exprPath: ExpressionPath;
  fieldExpr: Expression | undefined;
  humanTexts: HumanTexts;
}

const FilterEntryContext = createContext<FilterEntryContextType | undefined>(
  undefined
);

const useFilterEntryContext = () => {
  const context = useContext(FilterEntryContext);
  if (!context) {
    throw new Error(
      'useFilterEntryContext must be used within a ValueBinderContext'
    );
  }
  return context;
};

const generateProplabel = (
  humanTexts: HumanTexts,
  operatorId: OperatorId
): string => {
  if (operatorId === '!=' || operatorId === '<' || operatorId === '>') {
    return `${humanTexts.fieldText || ''} ${humanTexts.operatorText || ''}`;
  }
  return humanTexts.fieldText || '';
};

type ValueBinderType = {
  bindingSourceMapping: BindingTypeData;
  children: React.ReactNode;
};

const ValueBinder = ({bindingSourceMapping, children}: ValueBinderType) => {
  const {
    initialCheckerState,
    expressionEditorDispatch,
    exprPath,
    fieldExpr,
    fieldPath,
    bindingSource,
    operatorId,
    humanTexts,
  } = useFilterEntryContext();

  const {fieldType, valueMapper, resetValue, updateOperator} =
    bindingSourceMapping;

  // For TriStateBoolean we don't want to take fieldExpr value into account because it is string
  // but we want to select TriStateBoolean values
  const checkTypeValue = valueMapper(fieldExpr, humanTexts);
  const [fieldCheckerState, fieldTypedCheckedExpr] = checkType(
    checkTypeValue,
    withSystem(
      {
        isBindable: true,
        label: generateProplabel(humanTexts, operatorId),
      },
      fieldType
    )
  )(initialCheckerState);

  const onBindingChange = useCallback(
    (updatedBinding: null | Expression, meta: ExpressionBindMetaPayload) => {
      if (updatedBinding) {
        let binding = updatedBinding;
        let key = exprPath;
        if (updateOperator) {
          [key, binding] = updateOperator(exprPath, fieldPath, updatedBinding);
        }

        expressionEditorDispatch({
          type: 'EXPRESSION_BIND_CHANGED',
          binding,
          meta,
          key,
        });
      } else {
        // atm there is no datatype defined for DynamoWrapper so EXPRESSION_BIND_CHANGED cannot reset it to default
        // clearing up value manually
        const newExpression = resetValue(bindingSource);
        expressionEditorDispatch({
          type: 'EXPRESSION_ACTION',
          payload: {
            newExpression,
            meta,
          },
          key: exprPath,
        });
      }
    },
    [
      bindingSource,
      resetValue,
      expressionEditorDispatch,
      exprPath,
      updateOperator,
      fieldPath,
    ]
  );

  return (
    <TypeCheckerState.Provider
      value={[fieldCheckerState, fieldTypedCheckedExpr]}
    >
      <ExpressionBinding
        expression={fieldTypedCheckedExpr}
        onChange={onBindingChange}
        bindUIVariant="dot"
        automationLabel="Field"
      >
        {children}
      </ExpressionBinding>
    </TypeCheckerState.Provider>
  );
};

const isEmptyString = matchExpression({
  Text: (val) => val.length === 0,
  _: constantFalse,
});

type FilterListItemProps = {
  listItem: {item: FilterItem; id: string};
  idx: number;
  bindingContext: ConsolidatedBindingContext;
  parentBindingContext: ConsolidatedBindingContext;
  stores: DynListFilterSettingsProps['stores'];
  dispatch: Dispatch;
  itemIndex: number;
  setItemIndex: (index: number) => void;
  queryParams: FilterItem[];
  getFieldInitialValues: (
    bindingSource: BindingSource | FauxBindingSource
  ) => any;
  currentComponent: Component.ComponentType;
  componentMap: Component.ComponentMap;
  currentPage: PageRecordType | null;
  isReferencedItemLoading?: boolean;
  checkerState: CheckerState;
  expressionEditorDispatch: (action: ExpressionEditorActionPayload) => void;
  filterExpr: Expression;
  plugins: Plugins.PluginsType;
};

const FilterListItem = ({
  listItem,
  idx,
  bindingContext,
  parentBindingContext,
  stores,
  dispatch,
  itemIndex,
  setItemIndex,
  queryParams,
  getFieldInitialValues,
  isReferencedItemLoading,
  checkerState,
  expressionEditorDispatch,
  filterExpr,
  plugins,
}: FilterListItemProps) => {
  const {
    item: {fieldPath, operator: operatorId, value, value2, value3},
  } = listItem;

  const exprPath: ExpressionPath = useMemo(
    () => [
      fieldsPathPart,
      {in: 'List', index: idx},
      {in: 'Record', at: 'value'},
    ],
    [idx]
  );

  const fieldExpr = Expressions.getIn(filterExpr, exprPath);

  const normalizedProps = useMemo(() => {
    return normalizePropsForRenderingWithBindingContexts({
      type: 'query',
      bindingContext,
      parentBindingContext,
      stores,
      fieldPath,
      operator: operatorId,
      value,
      value2,
      value3,
      // Will be handled separately
      onChange: () => {},
      onValueChange: () => {},
      onValue2Change: () => {},
      onValue3Change: () => {},
    });
  }, [
    stores,
    bindingContext,
    parentBindingContext,
    fieldPath,
    operatorId,
    value,
    value2,
    value3,
  ]);

  const humanTexts = useHumanTextsWithBindingContexts(
    new DynQueryParam({
      fieldPath,
      operator: operatorId,
      value,
      value2,
      value3,
    }),
    bindingContext,
    parentBindingContext,
    stores.collection,
    stores.localizationStore,
    isReferencedItemLoading || false
  );

  const {bindingSource, availableFieldOptions} = normalizedProps;

  // Show warning icon if filter is pass-through (has empty value so filter is ignored)
  const showWarningIcon = useMemo(
    () => fieldExpr && isEmptyString(fieldExpr),
    [fieldExpr]
  );
  if (!fieldExpr) return null;

  const bindingLabel = getBindingLabel(
    checkerState.env.context.values,
    fieldExpr
  );
  const bindingIcon =
    getBindingIcon(checkerState.env.context.values, fieldExpr, plugins) ||
    textIcon;

  // Render an empty list item, that does not open a popover, if
  // no binding source found for the field. This happens when a field
  // is deleted after a filter is added in.
  if (!bindingSource)
    return (
      <PropertyListItem
        data-automation-id="filter-item"
        key={idx}
        id={String(idx)}
        label={<span>Missing field</span>}
      />
    );

  return (
    <FilterEntryContext.Provider
      value={{
        initialCheckerState: checkerState,
        expressionEditorDispatch,
        listIdx: idx,
        bindingSource,
        fieldPath,
        parentBindingContext,
        operatorId,
        stores,
        dispatch,
        getFieldInitialValues,
        availableFieldOptions,
        exprPath,
        fieldExpr,
        humanTexts,
      }}
    >
      <DeprecatedPopoverBridge
        onChange={(state) => setItemIndex(state.open ? idx : -1)}
        state={idx === itemIndex ? OPENED_STATE : CLOSED_STATE}
        renderPopoverContent={() => (
          <View
            padding="$2"
            width={rightSidebarWidth}
            data-automation-id="filter-item-popover"
          >
            <FieldSelect bindingContext={bindingContext} />
            <OperatorSelect normalizedProps={normalizedProps} />
            {isValueRequired(bindingSource, operatorId) && (
              <ConditionValue
                // Remount component on field changes to prevent stale input state
                key={`${bindingSource.type}-${fieldPath}`}
                listItem={listItem}
                normalizedProps={normalizedProps}
              />
            )}
          </View>
        )}
        showArrow={false}
        showMask="TRANSPARENT"
        metricsIdentifier="dyn-list-filter-settings"
      >
        {({openPopover, Anchor}) => (
          <Anchor asChild={false}>
            {(ref: React.RefObject<any>) => (
              <PropertyListItem
                _ref={ref}
                data-automation-id="filter-item"
                key={idx}
                id={String(idx)}
                label={
                  <FilterLabel
                    bindingLabel={bindingLabel}
                    bindingIcon={bindingIcon}
                    isReferencedItemLoading={isReferencedItemLoading}
                    isFieldBinding={Expressions.isBinding(fieldExpr)}
                  />
                }
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  if (queryParams.length) openPopover(e);
                }}
                trailingIcon={
                  showWarningIcon && (
                    <Tooltip
                      label="Add a value to enable this filter"
                      placement="bottom-start"
                      minWidth={200}
                    >
                      <WarningTriangleIcon
                        data-automation-id="pass-through-warning"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    </Tooltip>
                  )
                }
              />
            )}
          </Anchor>
        )}
      </DeprecatedPopoverBridge>
    </FilterEntryContext.Provider>
  );
};

type FilterSelectProps = {
  bindingContext: ConsolidatedBindingContext;
};

function fieldOption2SelectOption(option: FieldOption): SelectOption {
  const type = typeof option.type === 'string' ? option.type : 'PlainText';
  return {
    icon: <FieldListIcon type={type} innerType={option.innerType} />,
    label: option.name,
    value: option.value ?? '',
  };
}

const FieldSelect: React.FC<FilterSelectProps> = ({
  bindingContext,
}: FilterSelectProps) => {
  const {
    bindingSource,
    parentBindingContext,
    stores,
    fieldPath,
    listIdx,
    operatorId,
    getFieldInitialValues,
    availableFieldOptions,
    expressionEditorDispatch,
  } = useFilterEntryContext();

  const options: Array<SelectOption | SelectOptionGroup> = [];
  let groupOptions: SelectOption[] | undefined;

  for (const option of availableFieldOptions) {
    if (option.kind === 'heading') {
      groupOptions = [];
      options.push({
        label: option.label,
        options: groupOptions,
      });
    } else {
      const selectOption = fieldOption2SelectOption(option);
      (groupOptions ? groupOptions : options).push(selectOption);
    }
  }

  const onFieldChange = fieldChangeHandler({
    handleChange: (params: Partial<ParamPanelState>) => {
      const {fieldPath: newFieldPath, operator: newOperator} = params;
      if (!bindingSource || !newFieldPath || !newOperator) return;
      const newBindingSource = getBindingSource(newFieldPath, bindingContext);

      if (!newBindingSource) return;
      const initialValues = getFieldInitialValues(newBindingSource);

      // Convert operators for ItemRef fields to handle single/multiple references correctly
      // For ItemRef fields, we need to convert between single-item operators (EQ/NE)
      // and multi-item operators (IDIN/IDNIN) based on whether the referenced field
      // contains single or multiple items
      const convertedOperator =
        newBindingSource.type === 'ItemRef'
          ? convertItemRefFilterOperatorForPersisting(
              newOperator,
              initialValues.value,
              parentBindingContext
            )
          : newOperator;

      const formattedField = mapFieldsQuery(
        convertedOperator,
        initialValues.value,
        initialValues.value2,
        initialValues.value3,
        newBindingSource.type
      );

      expressionEditorDispatch({
        type: 'EXPRESSION_ACTION',
        key: [fieldsPathPart, {in: 'List', index: listIdx}],
        payload: {
          newExpression: ERecord({
            fieldPath: EText(newFieldPath),
            operatorName: EText(formattedField.operatorName),
            value: EText(String(formattedField.value)),
          }),
        },
      });
    },
    bindingContext,
    parentBindingContext,
    operator: operatorId,
    collectionState: stores.collection,
    currentLocale: stores.localizationStore.currentLocale,
  });

  return (
    <TwoColumns>
      <span>Field</span>
      <Select
        id="filter-field"
        aria-label="Select a field"
        data-automation-id="filter-field-select"
        value={fieldPath}
        onChange={onFieldChange}
        options={options}
        shouldPositionInline
        shouldTruncateButtonText
        optionTruncationProps={{
          label: {truncateToLines: 1},
        }}
      />
    </TwoColumns>
  );
};

type OperatorSelectProps = {
  normalizedProps: ReturnType<typeof normalizePropsForRendering>;
};

const sentenceCase = (str: string) => {
  if (str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const OperatorSelect: React.FC<OperatorSelectProps> = ({
  normalizedProps,
}: OperatorSelectProps) => {
  const {
    bindingSource,
    operatorId,
    listIdx,
    fieldPath,
    expressionEditorDispatch,
    getFieldInitialValues,
  } = useFilterEntryContext();
  const convertedOperator = convertItemRefFilterOperatorForRendering(
    bindingSource.type,
    operatorId
  );

  const onOperatorChange = (operator: OperatorId) => {
    const values = getFieldInitialValues(bindingSource);

    const formattedField = mapFieldsQuery(
      operator,
      values.value,
      values.value2,
      values.value3,
      bindingSource.type
    );

    const canKeepCurrentValue =
      isComparisonOperator(operator) && isComparisonOperator(operatorId);
    if (canKeepCurrentValue) {
      expressionEditorDispatch({
        type: 'EXPRESSION_ACTION',
        key: [
          fieldsPathPart,
          {in: 'List', index: listIdx},
          {in: 'Record', at: 'operatorName'},
        ],
        payload: {
          newExpression: EText(formattedField.operatorName),
        },
      });
    } else {
      expressionEditorDispatch({
        type: 'EXPRESSION_ACTION',
        key: [fieldsPathPart, {in: 'List', index: listIdx}],
        payload: {
          newExpression: ERecord({
            fieldPath: EText(fieldPath),
            operatorName: EText(formattedField.operatorName),
            value: EText(String(formattedField.value)),
          }),
        },
      });
    }
  };

  let operatorBindingMapping;
  if (bindingSource.type === 'Bool') {
    operatorBindingMapping = bindingSourceToTypeMapping.Bool;
  } else if (
    operatorId === OPERATOR_EXISTS ||
    operatorId === OPERATOR_NOT_EXISTS
  ) {
    operatorBindingMapping = bindingSourceToTypeMapping.IsSet;
  }

  const operatorOptions = normalizedProps.availableOperators.map((op) => {
    return {
      label: sentenceCase(op.name),
      value: op.id,
    } as const;
  });

  const selectEl = (
    <Select
      id="filter-operator"
      data-automation-id="filter-operator-select"
      value={convertedOperator}
      onChange={(selectedValue) => onOperatorChange(selectedValue)}
      options={operatorOptions}
      shouldPositionInline
    />
  );

  return (
    <StyledInputRow>
      <span>When</span>
      {operatorBindingMapping ? (
        <ValueBinder bindingSourceMapping={operatorBindingMapping}>
          {selectEl}
        </ValueBinder>
      ) : (
        selectEl
      )}
    </StyledInputRow>
  );
};

const ensureId = (item: FilterItem, index: number) => ({
  item,
  id: String(index),
});

// TODO: Once the legacy usage of parseQuery is moved to POJOs,
// we can remove the imm state
type QueryParamsState = {
  pojo: FilterItem[];
  imm: List<DynQueryParamType>;
};

const DynListFilterSettingsCLP = ({
  dispatch,
  bindingContext,
  parentBindingContext,
  plugins,
  stores,
  currentComponent,
  componentMap,
  currentPage,
  checkerState,
  expressionEditorDispatch,
  strippedExpr,
  expr,
  exprAsUnique,
  defaultValueExpr,
}: DynListFilterSettingsProps) => {
  const filterFieldsExpr = Expressions.getIn(strippedExpr, fieldsPath);
  const [itemIndex, setItemIndex] = useState(-1);
  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    pojo: [],
    imm: List(),
  });
  const [bindingSources, setBindingSources] = useState<BindingSource[]>([]);
  const [queryParamsFilterForValue, setQueryParamsFilterForValue] =
    useState<QueryFilterStateType>(QUERY_FILTER_FOR_STATES.ALL);

  const getFieldInitialValues = useCallback(
    (bindingSource: BindingSource | FauxBindingSource) => {
      const fieldPath = getFieldPath(bindingSource);
      const fieldType = bindingSource.type;

      const operators = getOperatorOptions(
        fieldPath,
        fieldType,
        fieldType === 'Set' ? bindingSource.meta?.innerType : ''
      );

      const availableItemRefOptions = getItemRefOptions({
        bindingSource,
        parentBindingContext,
        collectionState: stores.collection,
        currentLocale: stores.localizationStore.currentLocale,
      });

      const availableOptionOptions = getOptionOptions(bindingSource);

      const {value, value2, value3} = getInitialValues({
        fieldType,
        availableItemRefOptions,
        availableOptionOptions,
      });

      return {
        fieldPath,
        fieldType,
        operators,
        availableItemRefOptions,
        availableOptionOptions,
        value,
        value2,
        value3,
      };
    },
    [
      parentBindingContext,
      stores.collection,
      stores.localizationStore.currentLocale,
    ]
  );

  useEffect(() => {
    if (!bindingContext) return;

    const filterForExpr = Expressions.getIn(strippedExpr, filterForPath);
    const filterForKey = filterForExpr
      ? Expressions.getText(filterForExpr)
      : null;
    const newFilterFor =
      filterForKey === QUERY_FILTER_FOR_STATES.ALL ||
      filterForKey === QUERY_FILTER_FOR_STATES.ANY
        ? filterForKey
        : QUERY_FILTER_FOR_STATES.ALL;
    setQueryParamsFilterForValue(newFilterFor);

    if (filterFieldsExpr) {
      // Enrich the eval env with component data defaults so that
      // expressions referencing the `data` variable (e.g. type
      // conversions like Boolean::["To Text"]) can resolve properly.
      const baseEnv = Plugins.getEvalEnv(plugins);
      const componentDataType = Component.getDataType(currentComponent);
      const dataFields = Types.getRecordFields(componentDataType);
      let evalEnv = baseEnv;
      if (dataFields) {
        const defaults: Record<string, unknown> = {};
        for (const key of Object.keys(dataFields)) {
          const fieldType = dataFields[key];
          if (fieldType) {
            defaults[key] = Types.getTypeDefaultValue(plugins)(fieldType);
          }
        }
        evalEnv = EvalEnv.insertValue(
          baseEnv,
          Variable.unqualified(identifier('data')),
          defaults
        );
      }
      const queryFields = Expressions.evaluate(evalEnv, filterFieldsExpr);
      const parsedQueryParams = parseQuery(queryFields, bindingContext);
      setQueryParams({
        pojo: parsedQueryParams.toJS(),
        imm: parsedQueryParams,
      });
    } else {
      setQueryParams({pojo: [], imm: List()});
    }
  }, [
    bindingContext,
    plugins,
    strippedExpr,
    filterFieldsExpr,
    currentComponent,
  ]);

  useEffect(() => {
    setBindingSources(
      bindingContext ? getFilterBindingSources(bindingContext) : []
    );
  }, [bindingContext]);

  const handleQueryParamsFilterForSwitch = useCallback(
    (value: QueryFilterStateType) => {
      expressionEditorDispatch({
        type: 'EXPRESSION_ACTION',
        key: filterForPath,
        payload: {
          newExpression: EText(value),
        },
      });
      setQueryParamsFilterForValue(value);
    },
    [expressionEditorDispatch]
  );

  const openAddItemModal = () => {
    setItemIndex(queryParams.pojo.length);
    const currentFilterFor = Expressions.getIn(strippedExpr, filterForPath);
    const currentList = filterFieldsExpr
      ? Expressions.getList(filterFieldsExpr) || []
      : [];
    expressionEditorDispatch({
      type: 'EXPRESSION_ACTION',
      payload: {
        newExpression: ERecord({
          filterFor: currentFilterFor || EText(QUERY_FILTER_FOR_STATES.ALL),
          fields: EList([
            ...currentList,
            ERecord({
              fieldPath: EText(createFieldPath('name')),
              operatorName: EText(getOperatorById(OPERATOR_EQ).name),
              value: EText(''),
            }),
          ]),
        }),
      },
      key: [],
    });
  };

  const deleteItem = (index: PropertyListItemID) => {
    const currentFilterFor = Expressions.getIn(strippedExpr, filterForPath);
    const currentList = filterFieldsExpr
      ? Expressions.getList(filterFieldsExpr) || []
      : [];
    const newList = deleteAt(currentList, Number(index));
    const newFilterFor =
      !currentFilterFor || newList.length === 0
        ? EText(QUERY_FILTER_FOR_STATES.ALL)
        : currentFilterFor;
    const newExpr = ERecord({
      filterFor: newFilterFor,
      fields: EList(newList),
    });
    expressionEditorDispatch({
      type: 'EXPRESSION_ACTION',
      key: [],
      payload: {
        newExpression: newExpr,
      },
    });
  };

  const itemIdsByCollectionId = useMemo(() => {
    return getReferencedItemIds(bindingSources, queryParams.imm);
  }, [bindingSources, queryParams.imm]);

  const filterItems = queryParams.pojo.map(ensureId);

  const onBindingChange = (
    updatedBinding: null | Expression,
    meta: ExpressionBindMetaPayload
  ) => {
    if (updatedBinding === null) {
      // atm there is no datatype defined for DynamoWrapper so EXPRESSION_BIND_CHANGED cannot reset it to default
      // clearing up value manually. We also need to ensure we are purely resetting to a List, not a Distinct
      const defaultValueExprNoDistinct = matchExpression({
        Distinct: ({value}) => value,
        _: identity,
      })(defaultValueExpr);

      expressionEditorDispatch({
        type: 'EXPRESSION_ACTION',
        payload: {
          newExpression: defaultValueExprNoDistinct,
          meta,
        },
        key: [],
      });
    } else {
      expressionEditorDispatch({
        type: 'EXPRESSION_BIND_CHANGED',
        binding: updatedBinding,
        meta,
        key: [],
      });
    }
  };

  if (ContextualExpressions.isBinding(stripAnn(expr))) {
    return (
      <View>
        <HStack data-automation-id="collection-list-settings-filters-group">
          <Text
            colorVariant="secondary"
            style={{
              width: rightSidebarLabelWidth,
              minWidth: rightSidebarLabelWidth,
            }}
          >
            Filters
          </Text>
          <ExpressionBinding
            expression={exprAsUnique}
            onChange={onBindingChange}
            bindUIVariant="dot"
            automationLabel="Filter"
            style={{width: '100%'}}
          ></ExpressionBinding>
        </HStack>
      </View>
    );
  }
  return (
    <View>
      <HStack
        data-automation-id="collection-list-settings-filters-group"
        spacing={1}
      >
        <Text colorVariant="secondary">Filters</Text>
        <ExpressionBinding
          expression={exprAsUnique}
          onChange={onBindingChange}
          bindUIVariant="dot"
          automationLabel="Filter"
          button={(openPopover, targetRef) => (
            <Hitarea>
              <SymbolBindingDot
                ref={targetRef as React.RefObject<HTMLDivElement>}
                data-automation-id="binding-button_Filter"
                clickable
                onClick={openPopover}
                style={{
                  position: 'absolute',
                  top: -10,
                  left: -4,
                }}
              />
            </Hitarea>
          )}
        ></ExpressionBinding>

        <Spacer />
        {queryParams.pojo.length > 1 && (
          <QueryFilterForDropdown
            onChange={handleQueryParamsFilterForSwitch}
            value={queryParamsFilterForValue}
          />
        )}
        <PropertyListAddButton
          tooltip="Add filter"
          data-automation-id="collection-list-settings-add-filter"
          onClick={openAddItemModal}
        />
      </HStack>

      {filterItems.length > 0 && (
        <View marginTop="$1">
          <CollectionItemsLoader itemIdsByCollectionId={itemIdsByCollectionId}>
            {(areItemsLoading) => (
              <PropertyList
                automationId="collection-list-settings-filters-list"
                sortable={false}
                deletable
                onItemDeleted={deleteItem}
                items={filterItems}
                renderItem={(listItem, idx) => (
                  <FilterListItem
                    listItem={listItem}
                    idx={idx}
                    bindingContext={bindingContext!}
                    parentBindingContext={parentBindingContext!}
                    stores={stores}
                    dispatch={dispatch}
                    itemIndex={itemIndex}
                    setItemIndex={setItemIndex}
                    queryParams={queryParams.pojo}
                    getFieldInitialValues={getFieldInitialValues}
                    currentComponent={currentComponent}
                    componentMap={componentMap}
                    currentPage={currentPage}
                    isReferencedItemLoading={
                      areItemsLoading &&
                      isReferenceBindingSource(
                        listItem.item.fieldPath,
                        bindingSources
                      )
                    }
                    checkerState={checkerState}
                    expressionEditorDispatch={expressionEditorDispatch}
                    filterExpr={strippedExpr}
                    plugins={plugins}
                  />
                )}
              />
            )}
          </CollectionItemsLoader>
        </View>
      )}
    </View>
  );
};
